import React, { useState } from 'react';
import { 
  PageHeader, 
  SectionCard, 
  DataTable, 
  ConfirmDialog 
} from '../../components/common';
import { theme } from '../../design/theme';

interface Company {
  id: string;
  name: string;
  adminEmail: string;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
}

export const CompanyManagement: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([
    { id: 'comp-1', name: 'Acme Corp', adminEmail: 'admin@acme.com', status: 'ACTIVE', createdAt: '2025-01-15' },
    { id: 'comp-2', name: 'Globex', adminEmail: 'admin@globex.com', status: 'ACTIVE', createdAt: '2025-02-10' },
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({ name: '', adminEmail: '' });

  const handleAdd = () => {
    setEditingCompany(null);
    setFormData({ name: '', adminEmail: '' });
    setIsModalOpen(true);
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({ name: company.name, adminEmail: company.adminEmail });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      setCompanies(companies.filter(c => c.id !== id));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCompany) {
      setCompanies(companies.map(c => c.id === editingCompany.id ? { ...c, ...formData } : c));
    } else {
      const newCompany: Company = {
        id: `comp-${Math.random().toString(36).substr(2, 5)}`,
        name: formData.name,
        adminEmail: formData.adminEmail,
        status: 'ACTIVE',
        createdAt: new Date().toISOString().split('T')[0]
      };
      setCompanies([...companies, newCompany]);
    }
    setIsModalOpen(false);
  };

  const columns = [
    { header: 'NAME', accessor: 'name' as const },
    { header: 'ADMIN EMAIL', accessor: 'adminEmail' as const },
    { header: 'STATUS', accessor: 'status' as const },
    { header: 'CREATED AT', accessor: 'createdAt' as const },
    {
      header: 'ACTIONS',
      accessor: 'id' as const,
      render: (_: string, row: Company) => (
        <div style={{ display: 'flex', gap: theme.spacing.sm }}>
          <button onClick={() => handleEdit(row)} style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', color: 'var(--text-primary)' }}>Edit</button>
          <button onClick={() => handleDelete(row.id)} style={{ padding: '4px 8px', borderRadius: '4px', border: `1px solid ${theme.colors.critical.main}`, background: 'transparent', color: theme.colors.critical.main, cursor: 'pointer' }}>Delete</button>
        </div>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Company Management" 
        subtitle="Manage all enterprise clients and their administrative access."
        actions={<button onClick={handleAdd} style={{ backgroundColor: theme.colors.brand.main, color: '#FFFFFF', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}>Add Company</button>}
      />

      <SectionCard>
        <DataTable columns={columns} data={companies} />
      </SectionCard>

      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: theme.spacing.xl,
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: theme.shadows.lg
          }}>
            <h3 style={{ ...theme.typography.h3, marginBottom: theme.spacing.lg }}>{editingCompany ? 'Edit Company' : 'Add New Company'}</h3>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                <label style={theme.typography.caption}>Company Name</label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={e => setFormData({ ...formData, name: e.target.value })} 
                  required 
                  style={{ padding: theme.spacing.md, borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-default)', color: 'var(--text-primary)' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                <label style={theme.typography.caption}>Admin Email</label>
                <input 
                  type="email" 
                  value={formData.adminEmail} 
                  onChange={e => setFormData({ ...formData, adminEmail: e.target.value })} 
                  required 
                  style={{ padding: theme.spacing.md, borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-default)', color: 'var(--text-primary)' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.md, marginTop: theme.spacing.lg }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>Cancel</button>
                <button type="submit" style={{ backgroundColor: theme.colors.brand.main, color: '#FFFFFF', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}>{editingCompany ? 'Save Changes' : 'Create Company'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
